/* eslint-disable react/no-array-index-key  */
import {css} from '@mui/material';
import {
  ArrayUtils,
  AuthorizationFetcher,
  ConfigObjectType,
  DlMuiButton,
  DlMuiDynamicIcon,
  FetcherUtils,
  HierarchyMetadata,
  LoadingSpinner,
  omitNullsy,
  RISKINESS_COLORS,
  StringUtils,
  TypeWithEtag,
  useDefaultScrollTo,
  useLocalStorage,
} from '@wallaby-ui/core';
import {
  AG_GRID_COMPATIBILITY,
  DynamicChunk,
  JustTooltipFormatter,
  LightSelectedFilters,
  LightTable,
  MerieSchema,
  TableSorting,
  TooltipWrapper,
  useDrpCauseBackground,
  useFormatters,
  WallabyCoreSchema,
  RiskFactorTypeEnum,
  RiskFactorLabelEnum,
  ChipsFormatter,
  getFollowUpDateIconTooltip,
  usePermissions,
  getRecommendationTooltip,
  useDRPMedicationData,
  GetDRPMedicationDataProps,
} from '@wallaby-ui/document';
import {isEqual} from 'lodash';
import React, {useCallback, useEffect, useMemo} from 'react';

import {HospitalizationState} from './HospitalizationDetailLayoutV2';
import {HospitalizationPhRecommendationV2} from './HospitalizationPhRecommendationV2';
import {usePastUncheckedPhRecUpdater} from './PastUncheckedHospitalizationPhRecommendationsUpdaterV2';

type PharmaRecommendationCreateUpdateDto = MerieSchema.components['schemas']['PharmaRecommendationCreateUpdateDto'];

export const DEFAULT_PH_REC_SORTING: TableSorting = {
  field: 'date',
  direction: 'desc',
};
export type HospitalizationPhRecommendationProps = {
  state: HospitalizationState;
  changeState: (diff: Partial<HospitalizationState>) => void;
  storageIdPrefix: string;
  config: ConfigObjectType;
  hospitalizationId: string;
  defaultDepartmentId: number | null;
  riskFactorToRiskFactorLabel: Record<RiskFactorTypeEnum, RiskFactorLabelEnum>;
  patientCardLoading: boolean;
  encounterRiskFactors: MerieSchema.components['schemas']['RiskFactorDto'][];
  pharmaRecommendationsLoading: boolean;
  pharmaRecommendations: TypeWithEtag<MerieSchema.components['schemas']['PharmaRecommendationsDto']> | undefined;
  pharmaRecommendationsRefetch: () => void;
  reloadTimeline: () => void;
};
export type PhRecommendation = MerieSchema.components['schemas']['PharmaRecommendationDto'];
export type PhRecDRP = MerieSchema.components['schemas']['DrugRelatedProblemDto'];
export function getPhRecommendationDate(row: PhRecommendation): string {
  if (!row.isDirty && row.lastPublishDate) return row.lastPublishDate;
  return row.lastModifiedDate as string;
}
export function getIsPhRecUnpublished(phRec: Pick<PhRecommendation, 'isDirty'> | undefined): boolean {
  return phRec?.isDirty ?? true;
  // return phRec?.lastPublishDate == null || !(phRec?.lastPublishDate >= phRec?.lastModifiedDate);
}
export type MedicationType = WallabyCoreSchema.components['schemas']['MedicationExportDto'];
export type MedicationAtc = WallabyCoreSchema.components['schemas']['AtcHierarchyNodeWithParentDto'] & HierarchyMetadata;
export type DRPMedicationData = {
  code: string;
  atcLabel?: string;
  name: string;
  strength?: string;
  fullMedicationLabel: string;
};

type RecPanelState = {
  addEditOpen: boolean;
  openedItemId: PhRecommendation['id'] | undefined;
};
const DEFAULT_REC_PANEL_STATE: RecPanelState = {
  addEditOpen: false,
  openedItemId: undefined,
};

export function pickGetDRPMedicationDataProps(v: PhRecDRP): GetDRPMedicationDataProps {
  return {
    medicationId: v.medicationId,
    medicationUserInput: v.medicationUserInput,
    medicationAtcId: v.medicationAtcId,
    medicationName: v.medicationName,
  };
}

export const HospitalizationPhRecommendationPanelV2: React.FC<HospitalizationPhRecommendationProps> = (props) => {
  const {
    state,
    changeState,
    storageIdPrefix,
    config,
    hospitalizationId,
    defaultDepartmentId,
    riskFactorToRiskFactorLabel,
    patientCardLoading,
    encounterRiskFactors,
    pharmaRecommendations,
    pharmaRecommendationsRefetch,
    pharmaRecommendationsLoading,
    reloadTimeline,
  } = props;
  const [recPanelState, _, setRecPanelState] = useLocalStorage(`${storageIdPrefix}.recPanelState`, DEFAULT_REC_PANEL_STATE);
  useEffect(() => {
    const id = state.phRecItem?.openedItemId;
    if (id !== undefined) {
      setRecPanelState({addEditOpen: true, openedItemId: id});
      changeState({phRecItem: undefined});
    }
  }, [state.phRecItem?.openedItemId, setRecPanelState, changeState]);

  const [tableDefaultScroll, tableSetDefaultScroll] = useLocalStorage(`${storageIdPrefix}.phRecListScroll`, 0);
  const tableOnScroll = (event: Event) => tableSetDefaultScroll((event.target as HTMLElement).scrollTop);

  const [phRecDefaultDynamicChunks, setPhRecDefaultDynamicChunks] = useLocalStorage(
    `${storageIdPrefix}.phRecDynamicChunks`,
    [] as DynamicChunk[]
  );
  const phRecDynamicChunksOnChange = useCallback(
    (newDynamicChunks: DynamicChunk[]) => {
      setPhRecDefaultDynamicChunks(newDynamicChunks);
    },
    [setPhRecDefaultDynamicChunks]
  );

  const formatters = useFormatters();
  const {t, tEnum, formatDate} = formatters;
  const phRecListEtag = pharmaRecommendations?._etag;
  const {getDRPMedicationData, medications, isLoading: dprMedicationDataLoading} = useDRPMedicationData(config);

  const phRecommendationList = useMemo(() => {
    return pharmaRecommendations?.pharmaRecommendations ?? [];
  }, [pharmaRecommendations?.pharmaRecommendations]);
  const sortedPhRecommendationList = useMemo(() => {
    const phRecListWithSortedAtcs = phRecommendationList.map((phRec) => ({
      ...phRec,
      drugRelatedProblems: ArrayUtils.sortedBy(phRec.drugRelatedProblems, (v) => {
        const {fullMedicationLabel} = getDRPMedicationData(pickGetDRPMedicationDataProps(v));
        return [fullMedicationLabel];
      }),
    }));
    return ArrayUtils.sortedBy(phRecListWithSortedAtcs, (v) => [getPhRecommendationDate(v)], [state.phRecSorting.direction === 'desc']);
  }, [phRecommendationList, getDRPMedicationData, state.phRecSorting]);

  const hasSorting = useMemo(() => !isEqual(omitNullsy(state.phRecSorting), omitNullsy(DEFAULT_PH_REC_SORTING)), [state.phRecSorting]);
  const DRP_FLEX_COLUMN: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    maxWidth: '100%',
  };
  const DRP_GAP = 8;
  const DRP_LINE_HEIGHT = 20;
  const DRP_FONT_SIZE = 12;
  const DRP_PAD_Y = 0;
  const DRP_CHIP_GAP = 4;

  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.phRecScroll`, 0);
  const onScroll = (event) => setDefaultScroll((event.target as HTMLElement).scrollTop);
  const scrollWrapperRef = useDefaultScrollTo(defaultScroll);

  const showAddEditDialog = useCallback(
    (openedItemId: PhRecommendation['id'] | undefined) => {
      setDefaultScroll(0);
      setRecPanelState({
        addEditOpen: true,
        openedItemId,
      });
    },
    [setDefaultScroll, setRecPanelState]
  );
  const closeAddEditDialog = useCallback(
    (needRefetch: boolean) => {
      setRecPanelState(DEFAULT_REC_PANEL_STATE);
      if (needRefetch) {
        pharmaRecommendationsRefetch();
        reloadTimeline();
      }
    },
    [setRecPanelState, pharmaRecommendationsRefetch, reloadTimeline]
  );
  const openedItem = phRecommendationList.find((v) => v.id === recPanelState.openedItemId);
  const getDrpCauseBackground = useDrpCauseBackground();
  const addPhRecUrl = config.service.url.hospitalizationPhRecommendationCreate;
  const editPhRecUrl = config.service.url.hospitalizationPhRecommendationEdit;
  const createOrUpdatePhRec = useCallback(
    (phRec: PharmaRecommendationCreateUpdateDto) => {
      const isEdit = phRec.id != null;
      let apiUrl = isEdit ? editPhRecUrl : addPhRecUrl;
      apiUrl = apiUrl.replace('{evaluatedEncounterId}', hospitalizationId);
      apiUrl = apiUrl.replace('{pharmaRecId}', phRec.id);
      const apiMethod = isEdit ? 'put' : 'post';
      return AuthorizationFetcher.interceptedFetch(
        apiUrl,
        {
          method: apiMethod,
          body: JSON.stringify(phRec),
        },
        {
          'Content-Type': 'application/json',
        }
      )
        .then((response) => {
          return FetcherUtils.processJsonResponse(response);
        })
        .catch(StringUtils.printErrorAndRethrow);
    },
    [addPhRecUrl, editPhRecUrl, hospitalizationId]
  );
  const {checkPhRecChecked, modal: checkPhRecModal} = usePastUncheckedPhRecUpdater({
    items: pharmaRecommendations?.pharmaRecommendations,
    updatePhRec: createOrUpdatePhRec,
    pharmaRecommendationsRefetch,
  });

  const {userRoles} = usePermissions(config);
  return (
    <div data-fill-available style={{position: 'relative'}}>
      <div data-fill-available>
        <div data-fill-available style={{gap: 8}} ref={scrollWrapperRef} onScroll={onScroll}>
          {recPanelState.addEditOpen ? (
            pharmaRecommendationsLoading || patientCardLoading ? (
              <LoadingSpinner isVisible marginTop={8} />
            ) : (
              <HospitalizationPhRecommendationV2
                key={`item-${recPanelState.openedItemId}`}
                storageIdPrefix={`${storageIdPrefix}.${recPanelState.openedItemId ?? 'new'}`}
                onClose={closeAddEditDialog}
                item={openedItem}
                encounterRiskFactors={encounterRiskFactors}
                medications={medications}
                getDRPMedicationData={getDRPMedicationData}
                config={config}
                hospitalizationId={hospitalizationId}
                defaultDepartmentId={defaultDepartmentId}
                riskFactorToRiskFactorLabel={riskFactorToRiskFactorLabel}
                phRecListEtag={phRecListEtag}
              />
            )
          ) : (
            <>
              <div className='dl-header-buttons-row' style={{justifyContent: 'flex-end', gap: 8}}>
                <LightSelectedFilters
                  width='auto'
                  hasSorting={hasSorting}
                  handleResetSorting={() => changeState({phRecSorting: DEFAULT_PH_REC_SORTING})}
                />
                <DlMuiButton
                  type='chip-filled'
                  title={'hospitalization.detail.phRec.addNew'}
                  onClick={(event) => checkPhRecChecked(() => showAddEditDialog(undefined), event.target)}
                  disabled={!userRoles.ENCOUNTER_DETAIL_EDIT_ROLE}
                />
                {checkPhRecModal}
              </div>
              <LightTable
                data-fill-available
                style={{flex: 1}}
                isLoading={pharmaRecommendationsLoading || dprMedicationDataLoading}
                data={sortedPhRecommendationList}
                defaultScroll={{to: tableDefaultScroll}}
                onScroll={tableOnScroll}
                columns={[
                  {
                    field: 'date',
                    title: t('hospitalization.detail.phRec.date'),
                    render: (row, _compat) => {
                      const isUnpublished = getIsPhRecUnpublished(row);
                      const dateString = formatDate(row.date ?? '');
                      const riskiness = row?.riskiness;
                      const checkedFollowUpDate = row?.checked ? undefined : row?.followUpDate;
                      const tooltip = getRecommendationTooltip(
                        isUnpublished,
                        checkedFollowUpDate,
                        row?.lastPublishDate,
                        row?.conclusion,
                        riskiness,
                        formatters
                      );
                      const {iconName: followUpIconName} = getFollowUpDateIconTooltip(checkedFollowUpDate, formatters);

                      return (
                        <TooltipWrapper
                          tooltip={tooltip}
                          wrapperStyle={{
                            paddingBlock: DRP_PAD_Y,
                            display: 'flex',
                            alignItems: 'start',
                            flexDirection: 'column',
                          }}
                          popperInputCss={css`
                            max-width: 360px;
                          `}
                        >
                          <DlMuiButton
                            type='text-gray'
                            regular
                            title={dateString}
                            onClick={() => {
                              showAddEditDialog(row.id);
                            }}
                          />
                          <span
                            style={{fontSize: 'var(--dlFontSizeSmaller)', display: 'flex', alignItems: 'center', pointerEvents: 'none'}}
                          >
                            {riskiness && (
                              <ChipsFormatter
                                value={[riskiness]}
                                getInfo={(riskiness) => ({
                                  label: tEnum('Riskiness', riskiness),
                                  background: RISKINESS_COLORS[riskiness]?.background,
                                })}
                              />
                            )}
                            {followUpIconName && (
                              <DlMuiDynamicIcon
                                iconName={followUpIconName}
                                useFixedViewBox='0 0 14 16'
                                noButtonStyles
                                style={{padding: 0, marginLeft: 4, marginBottom: 2}}
                                svgIconStyle={{
                                  width: 16,
                                  height: 16,
                                }}
                              />
                            )}
                            {isUnpublished && (
                              <DlMuiDynamicIcon
                                iconName='merieSpikedExclamationIcon'
                                useFixedViewBox={14}
                                style={{padding: 0, marginLeft: 4, marginBottom: 2}}
                                svgIconStyle={{
                                  width: 16,
                                  height: 16,
                                }}
                              />
                            )}
                          </span>
                        </TooltipWrapper>
                      );
                    },
                    width: 95 + 26,
                    sorting: true,
                  },
                  {
                    field: 'medication',
                    title: t('hospitalization.detail.phRec.medication'),
                    minWidth: 180, // PDH-1589: readable floor; below it the table scrolls instead of collapsing the column
                    render: (row, _compat) => (
                      // REFACTOR: use same formatters as in all rec list?
                      <div style={{...DRP_FLEX_COLUMN, rowGap: DRP_GAP}}>
                        {row.drugRelatedProblems.map((drp, i) => {
                          const drpHeight = Math.max(0, (DRP_LINE_HEIGHT + DRP_PAD_Y + DRP_CHIP_GAP) * drp.causes.length - DRP_CHIP_GAP);
                          const {fullMedicationLabel} = getDRPMedicationData(pickGetDRPMedicationDataProps(drp));
                          return (
                            <React.Fragment key={i}>
                              {i !== 0 && <MedicationDivider widthOffset={22} />}
                              <div
                                style={{
                                  maxWidth: '100%',
                                  height: drpHeight,
                                  overflow: 'hidden',
                                  marginTop: -DRP_CHIP_GAP / 2,
                                  marginBottom: DRP_CHIP_GAP / 2,
                                  position: 'relative',
                                }}
                              >
                                <JustTooltipFormatter
                                  {...AG_GRID_COMPATIBILITY}
                                  style={{
                                    lineHeight: `${DRP_LINE_HEIGHT + DRP_PAD_Y + DRP_CHIP_GAP}px`,
                                    display: 'inline-block',
                                    height: 'fit-content',
                                  }}
                                  singleLine
                                  textStyle={{
                                    paddingBlock: 0,
                                    whiteSpace: 'unset',
                                    lineClamp: drp.causes.length,
                                    display: '-webkit-box',
                                    WebkitLineClamp: drp.causes.length,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                  value={fullMedicationLabel}
                                />
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    ),
                  },
                  {
                    field: 'causeOfDrp',
                    title: t('hospitalization.detail.phRec.causeOfDrp'),
                    tooltip: t('hospitalization.detail.phRec.causeOfDrp.long'),
                    minWidth: 180, // PDH-1589: readable floor; below it the table scrolls instead of collapsing the column
                    render: (row, _compat) => (
                      <div style={{...DRP_FLEX_COLUMN, rowGap: DRP_GAP}}>
                        {row.drugRelatedProblems.map((drp, i) => {
                          return (
                            <React.Fragment key={i}>
                              {i !== 0 && <MedicationDivider />}
                              <div style={{...DRP_FLEX_COLUMN, rowGap: DRP_CHIP_GAP}}>
                                {drp.causes.map((cause, j) => (
                                  <JustTooltipFormatter
                                    {...AG_GRID_COMPATIBILITY}
                                    key={j}
                                    style={{
                                      borderRadius: 2,
                                      padding: '0 4px',
                                      background: getDrpCauseBackground(cause),
                                      display: 'inline-block',
                                      height: DRP_LINE_HEIGHT,
                                      lineHeight: `${DRP_LINE_HEIGHT}px`,
                                      fontSize: DRP_FONT_SIZE,
                                    }}
                                    singleLine
                                    textStyle={{paddingBlock: DRP_PAD_Y / 2}}
                                    value={tEnum('DrpCause', cause)}
                                  />
                                ))}
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    ),
                  },
                ]}
                controlledSort={state.phRecSorting}
                onOrderChange={(field, direction) => {
                  changeState({phRecSorting: field ? {field, direction} : undefined});
                }}
                useDynamicRows
                defaultDynamicChunks={phRecDefaultDynamicChunks}
                dynamicChunksOnChange={phRecDynamicChunksOnChange}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
type MedicationDividerProps = {
  widthOffset?: number;
};
const MedicationDivider: React.FC<MedicationDividerProps> = (props) => {
  const {widthOffset = 0} = props;
  const MEDICATION_DIVIDER_HEIGHT = 1;
  return (
    <div
      style={{
        width: '100%',
        height: MEDICATION_DIVIDER_HEIGHT,
        position: 'relative',
      }}
    >
      <hr
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          margin: 0,
          width: `calc(100% + ${widthOffset}px)`,
          height: MEDICATION_DIVIDER_HEIGHT,
          background: 'var(--dlThemeGray-300)',
        }}
      />
    </div>
  );
};
